<template>
  <ContentBlock :required="true">
    <div slot="instructions">
      <h5>{{ $t('Content Basics') }}</h5>
      <p>{{ $t("Your title's basic information.") }}</p>
    </div>
    <div class="basics">
      <UIInput
        class="content-title"
        :title="$t('Content Title')"
        required
        maxlength="255"
        v-model="contentOwnership.title"
      />

      <div class="creators">
        <UIInput
          class="developers"
          maxlength="255"
          :title="$t('Developers')"
          required
          v-model="contentOwnership.developers"
        />
        <UIInput
          class="publishers"
          maxlength="255"
          :title="$t('Publishers')"
          v-model="contentOwnership.publishers"
        />
      </div>
      <UIInput
        :title="$t('Primary Support Email')"
        :instructions="$t('This is only available to organizations that have licensed your content')"
        type="email"
        v-model="contentOwnership.developerSupportEmail"
        required
      ></UIInput>
      <UIInput
        :textarea="true"
        rows="15"
        maxlength="1000000"
        class="description"
        :title="$t('Long Description')"
        :instructions="$t('Users will see this text when browsing your content the marketplace.')"
        required
        v-model="contentOwnership.description"
      />

      <div class="headset-type">
        <div class="label f8 mb3">
          {{ $t('Headsets Supported') }}
          <span class="required">*</span>
        </div>
        <div class="buttons ba b--geyser ph5 pv4 br3">
          <UICheckbox
            v-model="contentOwnership.headsetHtcVive"
            :class="{ active: contentOwnership.headsetHtcVive }"
            :title="$t('HTC Vive')"
          ></UICheckbox>
          <UICheckbox
            v-model="contentOwnership.headsetOculusRift"
            :class="{ active: contentOwnership.headsetOculusRift }"
            :title="$t('Oculus Rift')"
          ></UICheckbox>
          <UICheckbox
            v-model="contentOwnership.headsetWindowsMr"
            :class="{ active: contentOwnership.headsetWindowsMr }"
            :title="$t('WMR')"
          ></UICheckbox>
          <UICheckbox
            v-model="contentOwnership.headsetValveIndex"
            :class="{ active: contentOwnership.headsetValveIndex }"
            :title="$t('Valve Index')"
          ></UICheckbox>
        </div>
      </div>

      <UISelect
        class="experience-type"
        :title="$t('Experience Type')"
        v-model="contentOwnership.experienceType"
        :placeholder="$t('Choose an Experience Type')"
        required
      >
        <option value="room-scale">{{ $t('Room Scale / Standing') }}</option>
        <option value="free-roam">{{ $t('Free Roaming') }}</option>
        <option value="motion-platform">{{ $t('Motion Platform') }}</option>
        <option value="seated">{{ $t('Seated Only') }}</option>
      </UISelect>

      <div class="media">
        <div class="image">
          <UIFormItem
            :title="$t('Content Image')"
            :instructions="$t('This image will be seen by location operators when browsing your content on the marketplace and by end-users when browsing content in the headset.')"
            required
          ></UIFormItem>
          <FileInputWithThumbnail
            :ratio="46.5863"
            id="image"
            type="image"
            :media="getMedia('image')"
            class="mt2"
            :required="true"
            :specs="$t('25MB maximum, suggested size 600x280px.')"
            @input="onFileUploadInput"
            @clear="onFileUploadClear"
          />
        </div>
        <div class="video">
          <UIFormItem
            :title="$t('Content Trailer')"
            :required="true"
            :instructions="$t('This video will be seen by location operators when browsing your content on the marketplace and on the SpringboardVR Highlight Reel.')"
          ></UIFormItem>
          <FileInputWithThumbnail
            :ratio="46.5863"
            id="video"
            type="video"
            :media="getMedia('video')"
            class="mt2"
            :required="true"
            :specs="$t('25MB maximum, suggested size 600x280px.')"
            @input="onFileUploadInput"
            @clear="onFileUploadClear"
          />
        </div>
      </div>
      <UIFormItem :title="$t('System Requirements')" required>
        <TinyMCEEditor v-model="contentOwnership.systemRequirements" :options="{height: 200}" />
      </UIFormItem>
    </div>
  </ContentBlock>
</template>

<script>
import ContentBlock from '@/core/shared/components/ContentBlock';
import UIInput from '@/core/shared/components/ui/UIInput';
import UISelect from '@/core/shared/components/ui/UISelect';
import UICheckbox from '@/core/shared/components/ui/UICheckbox';
import UIFormItem from '@/core/shared/components/ui/UIFormItem';
import { useContentOwnershipMedia } from './compositions';
import TinyMCEEditor from './TinyMCEEditor';
import FileInputWithThumbnail from '@/core/shared/components/FileInputWithThumbnail';

export default {
  name: 'ContentBasicsForm',
  components: {
    ContentBlock,
    UIInput,
    UICheckbox,
    UISelect,
    TinyMCEEditor,
    UIFormItem,
    FileInputWithThumbnail,
  },
  props: {
    contentOwnership: {
      type: Object,
      required: true,
    },
  },
  setup (props) {
    return {
      ...useContentOwnershipMedia(props.contentOwnership),
    };
  },
};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.basics {
  & > * {
    margin-bottom: var(--spacingMd);

    &:last-child {
      margin-bottom: 0;
    }
  }

  & .required {
    color: var(--colorSBRedFlat);
  }

  & .creators {
    & .developers {
      margin-bottom: var(--spacingMd);
    }

    @media (--tablet) {
      display: flex;

      & > * {
        flex: 1 1 auto;

        &:first-child {
          margin-right: var(--spacingMd);
        }
      }

      & .developers {
        margin-bottom: 0;
      }
    }
  }

  & .label {
    margin-bottom: var(--spacingXS);
  }

  & .helper {
    padding: var(--spacingXS) 0;
  }

  & .headset-type {
    & .buttons {
      display: grid;
      grid-gap: var(--spacingSm);
      row-gap: var(--spacingSm);
      justify-content: space-between;
      @media (--tablet) {
        grid-template-columns: repeat(4, auto);
      }
    }
  }

  & .experience-type {
    @media (--tablet) {
      width: 50%;
    }
  }

  & .media {
    @media (--tablet) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: var(--spacingMd);
    }
  }
}
</style>
